import type { UserInfo } from 'firebase/auth';
import { useMemo } from 'react';
import { useLocalStorage } from 'react-use';
import { Providers, providerString } from '~/usecases/auth-use-case';
import { TWITTER_ID_STORAGE_KEY } from '~/utils/constants';

type SNSProviderAccount = {
  provider: string;
  id: string;
};

const partiallyHideString = (text: string): string => `***${text.slice(-2)}`;

const partiallyHideEmail = (email: string): string => {
  if (!email.includes('@')) {
    return '';
  }
  const domain = email.slice(email.indexOf('@'));
  const username = email.slice(0, email.indexOf('@'));
  return `${partiallyHideString(username)}${domain}`;
};

export const useSNSProviders = (providerData?: UserInfo[]) => {
  // Twitter id (the one after @, not the username) can only be retrieved after login
  // So it is stored in local storage in providerAuthenticationContainer
  // We show it here.
  const [getTwitterId] = useLocalStorage<string>(TWITTER_ID_STORAGE_KEY);

  const hasPhoneProvider = useMemo(
    () =>
      providerData &&
      providerData.findIndex((p) => p.providerId === 'phone') >= 0,
    [providerData]
  );
  const hasEmailProvider = useMemo(
    () =>
      providerData &&
      providerData.findIndex((p) => p.providerId === 'password') >= 0,
    [providerData]
  );

  const SNSProviders = useMemo<SNSProviderAccount[]>(() => {
    if (!providerData) return [];

    return providerData
      .filter((p) => p.providerId !== 'phone' && p.providerId !== 'password')
      .map((p): SNSProviderAccount => {
        // SNS Provider (not email, not phone)
        const id: string = (() => {
          switch (p.providerId) {
            case Providers.GOOGLE:
            case Providers.APPLE: {
              if (p.email) {
                return `${partiallyHideEmail(p.email)}`;
              }
              break;
            }
            case Providers.TWITTER: {
              if (getTwitterId) {
                return `@${partiallyHideString(getTwitterId)}`;
              } else if (p.displayName) {
                return `@${partiallyHideString(p.displayName)}`;
              }
              break;
            }
          }
          return '';
        })();
        return {
          id,
          provider: providerString(p.providerId as Providers),
        };
      });
  }, [getTwitterId, providerData]);

  return {
    SNSProviders,
    hasEmailProvider,
    hasPhoneProvider,
  };
};
